'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import Toast, { ToastProps } from '../ui/common/Toast';

/**
 * Anunciado sempre que um erro vai à tela. Consumidores em `features/` escutam sem
 * inverter a dependência de `shared/`.
 */
export const EVENTO_ERRO_EXIBIDO = 'arb:erro-exibido';
interface ToastContextType {
  showToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => void;
  showAlert: (config: {
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    description?: string;
    duration?: number;
  }) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
interface ToastProviderProps {
  children: ReactNode;
}
interface ToastItem extends ToastProps {
  id: string;
}
export const ToastProvider: React.FC<ToastProviderProps> = t0 => {
  const $ = _c(17);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [toasts, setToasts] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = id => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const removeToast = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = toast_0 => {
      const id_0 = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const newToast = {
        ...toast_0,
        id: id_0,
        onClose: removeToast
      };
      setToasts(prev_0 => {
        const updatedToasts = [newToast, ...prev_0];
        return updatedToasts.slice(0, 5);
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const showToast = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = t5 => {
      const {
        type,
        title,
        description,
        duration
      } = t5;
      const defaultDurations = {
        success: 5000,
        info: 5000,
        warning: 6000,
        error: 10000
      };
      showToast({
        type,
        title,
        message: description,
        duration: duration || defaultDurations[type]
      });
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const showAlert = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (title_0, message) => {
      showAlert({
        type: "success",
        title: title_0,
        description: message
      });
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const showSuccess = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (title_1, message_0) => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(EVENTO_ERRO_EXIBIDO));
      }
      showAlert({
        type: "error",
        title: title_1,
        description: message_0
      });
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const showError = t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (title_2, message_1) => {
      showAlert({
        type: "warning",
        title: title_2,
        description: message_1
      });
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const showWarning = t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (title_3, message_2) => {
      showAlert({
        type: "info",
        title: title_3,
        description: message_2
      });
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const showInfo = t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setToasts([]);
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const clearAllToasts = t9;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      showToast,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeToast,
      clearAllToasts
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const contextValue = t10;
  let t11;
  if ($[10] !== toasts) {
    t11 = toasts.map(_temp);
    $[10] = toasts;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t11) {
    t12 = <div className="pointer-events-none fixed right-6 bottom-6 z-[9999] flex w-full max-w-sm flex-col gap-3">{t11}</div>;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== children || $[15] !== t12) {
    t13 = <ToastContext.Provider value={contextValue}>{children}{t12}</ToastContext.Provider>;
    $[14] = children;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  return t13;
};
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast deve ser usado dentro de um ToastProvider");
  }
  return context;
};
function _temp(toast_1) {
  return <div key={toast_1.id} className="pointer-events-auto"><Toast {...toast_1} /></div>;
}